import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSupplierAuth } from '../../../context/SupplierAuthContext';
import { supplierModel } from '../../../models/supplierModel';
import supplierHelpdeskService from '../../../services/supplierHelpdeskService';
import { toast } from 'react-toastify';

const SupplierHelpdesk = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const { supplier } = useSupplierAuth();

    useEffect(() => {
        console.log('Supplier auth state:', supplier);
        if (supplier) {
            console.log('Supplier authenticated, loading tickets...');
            loadTickets();
        } else {
            console.log('No supplier authentication found');
        }
    }, [supplier]);

    const loadTickets = async () => {
        try {
            setLoading(true);
            const response = await supplierModel.getHelpdeskTickets();
            console.log('Helpdesk API response:', response);
            
            // Handle the standard API response format: { status: 1, data: { tickets: [], count: 0 } }
            if (response && response.status === 1 && response.data) {
                setTickets(response.data.tickets || []);
            } else if (response && response.tickets) {
                // Fallback for direct response format
                setTickets(response.tickets);
            } else {
                console.warn('Unexpected response format:', response);
                setTickets([]);
            }
        } catch (error) {
            console.error('Error loading tickets:', error);
            toast.error('Error loading tickets: ' + error.message);
            setTickets([]); // Set empty array on error
        } finally {
            setLoading(false);
        }
    };

    const getFilteredTickets = () => {
        if (filter === 'all') return tickets;
        return tickets.filter(ticket => ticket.status === filter);
    };

    const getStatusBadge = (status) => {
        const config = supplierHelpdeskService.getStatusConfig();
        const statusConfig = config[status] || { label: status, color: 'gray' };
        
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${supplierHelpdeskService.getStatusBadgeClass(status)}`}>
                {statusConfig.label}
            </span>
        );
    };

    const getPriorityBadge = (priority) => {
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${supplierHelpdeskService.getPriorityBadgeClass(priority)}`}>
                {priority?.toUpperCase()}
            </span>
        );
    };

    const getStatusCounts = () => {
        const counts = {
            all: tickets.length,
            open: tickets.filter(t => t.status === 'open').length,
            in_progress: tickets.filter(t => t.status === 'in_progress').length,
            closed: tickets.filter(t => ['resolved', 'closed'].includes(t.status)).length
        };
        return counts;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading tickets...</span>
            </div>
        );
    }

    const counts = getStatusCounts();
    const filteredTickets = getFilteredTickets();

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Support Tickets</h1>
                    <p className="text-gray-600 mt-1">
                        Manage your support requests and track their progress
                    </p>
                </div>
                <Link
                    to="/supplier/helpdesk/new"
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    New Ticket
                </Link>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center">
                        <div className="bg-blue-500 rounded-md p-3">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Tickets</p>
                            <p className="text-2xl font-semibold text-gray-900">{counts.all}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center">
                        <div className="bg-green-500 rounded-md p-3">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Open</p>
                            <p className="text-2xl font-semibold text-gray-900">{counts.open}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center">
                        <div className="bg-yellow-500 rounded-md p-3">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">In Progress</p>
                            <p className="text-2xl font-semibold text-gray-900">{counts.in_progress}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center">
                        <div className="bg-gray-500 rounded-md p-3">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Resolved</p>
                            <p className="text-2xl font-semibold text-gray-900">{counts.closed}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="bg-white rounded-lg shadow mb-6">
                <div className="border-b border-gray-200">
                    <nav className="flex space-x-8 px-6">
                        {[
                            { key: 'all', label: 'All Tickets', count: counts.all },
                            { key: 'open', label: 'Open', count: counts.open },
                            { key: 'in_progress', label: 'In Progress', count: counts.in_progress },
                            { key: 'closed', label: 'Resolved', count: counts.closed }
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setFilter(tab.key)}
                                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                                    filter === tab.key
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {tab.label}
                                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Tickets Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                {filteredTickets.length === 0 ? (
                    <div className="text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No tickets found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {filter === 'all' ? 'Get started by creating your first support ticket.' : `No tickets with ${filter.replace('_', ' ')} status.`}
                        </p>
                        {filter === 'all' && (
                            <div className="mt-6">
                                <Link
                                    to="/supplier/helpdesk/new"
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    Create Ticket
                                </Link>
                            </div>
                        )}
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Ticket
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Subject
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Priority
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredTickets.map((ticket) => (
                                <tr key={ticket.ticket_id} className="hover:bg-gray-50 transition-colors duration-150">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            #{ticket.ticket_number}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            ID: {ticket.ticket_id}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                                            {ticket.title}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {ticket.ticket_type?.replace('_', ' ').toUpperCase()}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {getPriorityBadge(ticket.priority)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {getStatusBadge(ticket.status)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {supplierHelpdeskService.formatDate(ticket.created_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <Link
                                            to={`/supplier/helpdesk/ticket/${ticket.ticket_id}`}
                                            className="text-blue-600 hover:text-blue-900 mr-4 transition-colors duration-200"
                                        >
                                            View Details
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Refresh Button */}
            <div className="mt-6 flex justify-end">
                <button
                    onClick={loadTickets}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                </button>
            </div>
        </div>
    );
};

export default SupplierHelpdesk;